import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaPlus, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import orderService, { computeTotals } from '../../utils/orderService';

function matchesQuery(order, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (String(order.id).includes(q)) return true;
  if (String(order.customerName || '').toLowerCase().includes(q)) return true;
  if (String(order.phone || '').toLowerCase().includes(q)) return true;
  if ((order.items || []).some((it) => String(it.name || '').toLowerCase().includes(q))) return true;
  return false;
}

const OrderList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setOrders(orderService.getAll());
  }, []);

  const refresh = () => setOrders(orderService.getAll());

  const filtered = useMemo(() => {
    return [...orders]
      .filter((o) => matchesQuery(o, query))
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [orders, query]);

  const handleAdd = () => navigate('/dashboard/orders/new');
  const handleView = (order) => navigate(`/dashboard/orders/${order.id}`);
  const handleDelete = (order) => {
    if (window.confirm(`Delete order #${order.id}?`)) {
      orderService.remove(order.id);
      refresh();
    }
  };

  const totals = useMemo(() => {
    const totalOrders = orders.length;
    const revenue = orders.reduce((sum, o) => sum + computeTotals(o).total, 0);
    return { totalOrders, revenue };
  }, [orders]);

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
            {t('dashboard.orders.title')}
          </p>
          <p className="mt-2 text-sm text-slate-600">
            {t('dashboard.orders.summary', { count: totals.totalOrders, amount: totals.revenue.toFixed(2) })}
            {' '}
            <span className="text-slate-400">|</span>{' '}
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('dashboard.orders.searchPlaceholder')}
            className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:w-[320px]"
          />
          <button
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00595A] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004446]"
          >
            <FaPlus className="h-4 w-4" />
            {t('dashboard.orders.addButton')}
          </button>
        </div>
      </div>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.orders.order')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.orders.customer')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.orders.phone')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.orders.items')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.orders.total')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {filtered.map((order) => {
              const orderTotals = computeTotals(order);
              return (
                <tr key={order.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">#{order.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{order.customerName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{order.phone || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{(order.items || []).length}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">${orderTotals.total.toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleView(order)}
                        className="inline-flex items-center justify-center rounded-md bg-slate-100 p-2 text-slate-700 transition hover:bg-slate-200"
                        aria-label={t('dashboard.common.view')}
                        title={t('dashboard.common.view')}
                      >
                        <FaEye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(order)}
                        className="inline-flex items-center justify-center rounded-md bg-rose-100 p-2 text-rose-800 transition hover:bg-rose-200"
                        aria-label={t('dashboard.common.delete')}
                        title={t('dashboard.common.delete')}
                      >
                        <FaTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {!filtered.length ? (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-500">
                  {t('dashboard.orders.noData')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 gap-3 md:hidden">
        {filtered.map((order) => {
          const orderTotals = computeTotals(order);
          return (
            <article key={order.id} className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                    {t('dashboard.orders.order')} #{order.id}
                  </p>
                  <h3 className="mt-2 text-base font-semibold text-slate-900">{order.customerName}</h3>
                  <p className="mt-1 text-sm text-slate-500">{order.phone || '-'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.orders.total')}</p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">${orderTotals.total.toFixed(2)}</p>
                  <p className="mt-1 text-sm text-slate-500">{t('dashboard.orders.cardItems', { count: (order.items || []).length })}</p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => handleView(order)}
                  className="inline-flex flex-1 items-center justify-center rounded-md bg-slate-100 px-3 py-3 text-slate-700 transition hover:bg-slate-200"
                  aria-label={t('dashboard.common.view')}
                  title={t('dashboard.common.view')}
                >
                  <FaEye className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(order)}
                  className="inline-flex flex-1 items-center justify-center rounded-md bg-rose-100 px-3 py-3 text-rose-800 transition hover:bg-rose-200"
                  aria-label={t('dashboard.common.delete')}
                  title={t('dashboard.common.delete')}
                >
                  <FaTrash className="h-4 w-4" />
                </button>
              </div>
            </article>
          );
        })}

        {!filtered.length ? (
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-left text-sm text-slate-500 shadow-sm">
            {t('dashboard.orders.noData')}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OrderList;
